import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, MapPin, Calendar, Briefcase, Mail, CheckCircle2 } from "lucide-react";
import api from "../../lib/api";
import { useSiteConfig } from "../../context/SiteConfigContext";
import SeoHead from "../../components/SeoHead";

export default function CareerDetail() {
    const { slug } = useParams();
    const { config } = useSiteConfig();
    const [job, setJob] = useState(null);
    const [error, setError] = useState(false);

    useEffect(() => {
        setJob(null); setError(false);
        api.get(`/jobs/${slug}`).then(({ data }) => setJob(data)).catch(() => setError(true));
    }, [slug]);

    if (error) return (
        <div className="max-w-3xl mx-auto px-6 py-32 text-center">
            <h1 className="font-display text-4xl mb-4">Position not found</h1>
            <p className="text-muted-foreground mb-8">This role may have closed.</p>
            <Link to="/careers" className="inline-flex items-center gap-2 px-6 py-3 rounded-sm bg-primary text-primary-foreground text-sm">
                <ArrowLeft size={14} /> Back to all positions
            </Link>
        </div>
    );
    if (!job || !config) return <div className="px-6 py-20 text-muted-foreground">Loading…</div>;

    const applyEmail = job.application_email || config.contact_email;

    return (
        <div data-testid={`career-detail-${job.slug}`}>
            <SeoHead
                pageKey="careers"
                title={`${job.title} — Careers at ${config.site_name}`}
                description={job.summary || `Open ${job.employment_type} position at YACRRD: ${job.title}`}
            />
            <article className="max-w-4xl mx-auto px-6 lg:px-10 py-20">
                <Link to="/careers" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary mb-8">
                    <ArrowLeft size={14} /> All positions
                </Link>

                <div className="text-xs uppercase tracking-[0.22em] text-secondary mb-3">
                    {job.employment_type}{job.department && ` · ${job.department}`}
                </div>
                <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mb-6">{job.title}</h1>

                <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-muted-foreground mb-10 pb-10 border-b border-border">
                    <span className="inline-flex items-center gap-2"><MapPin size={14} /> {job.location}</span>
                    <span className="inline-flex items-center gap-2"><Briefcase size={14} /> {job.employment_type}</span>
                    {job.deadline && <span className="inline-flex items-center gap-2"><Calendar size={14} /> Apply by {job.deadline}</span>}
                </div>

                {job.summary && <p className="text-lg text-foreground/90 mb-10 leading-relaxed">{job.summary}</p>}

                {job.description && (
                    <div className="prose max-w-none mb-10">
                        <h2 className="font-display text-2xl mb-3">About this role</h2>
                        <p className="whitespace-pre-line text-foreground/85 leading-relaxed">{job.description}</p>
                    </div>
                )}

                {job.responsibilities?.length > 0 && (
                    <div className="mb-10">
                        <h2 className="font-display text-2xl mb-4">Key responsibilities</h2>
                        <ul className="space-y-2.5">
                            {job.responsibilities.map((r, i) => (
                                <li key={i} className="flex items-start gap-3 text-foreground/85">
                                    <CheckCircle2 size={16} className="text-secondary mt-1 shrink-0" />
                                    <span>{r}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                {job.requirements?.length > 0 && (
                    <div className="mb-10">
                        <h2 className="font-display text-2xl mb-4">Requirements</h2>
                        <ul className="space-y-2.5">
                            {job.requirements.map((r, i) => (
                                <li key={i} className="flex items-start gap-3 text-foreground/85">
                                    <CheckCircle2 size={16} className="text-primary mt-1 shrink-0" />
                                    <span>{r}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                <div className="bg-muted/40 border border-border rounded-sm p-8 mt-12" data-testid="apply-section">
                    <h2 className="font-display text-2xl mb-3">How to apply</h2>
                    {job.how_to_apply && <p className="text-foreground/85 mb-5 whitespace-pre-line">{job.how_to_apply}</p>}
                    {applyEmail && (
                        <a
                            href={`mailto:${applyEmail}?subject=Application — ${encodeURIComponent(job.title)}`}
                            className="inline-flex items-center gap-2 px-6 py-3 rounded-sm bg-primary text-primary-foreground hover:bg-primary/90 text-sm font-medium"
                            data-testid="apply-cta"
                        >
                            <Mail size={14} /> Apply via email
                        </a>
                    )}
                </div>
            </article>
        </div>
    );
}
